import React, { useState, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { usePrefersReducedMotion } from '@hooks';

const StyledHeroSection = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: calc(100vh - var(--nav-height));
  padding-top: var(--nav-height);
  position: relative;

  @media (max-width: 768px) {
    min-height: calc(100vh - var(--nav-height));
    padding-top: var(--nav-height);
  }

  @media (max-width: 480px) {
    min-height: calc(100vh - var(--nav-height));
    padding-top: var(--nav-height);
  }

  h1 {
    margin: 0 0 30px 4px;
    color: var(--green);
    font-family: var(--font-sans);
    font-size: clamp(var(--fz-sm), 5vw, var(--fz-md));
    font-weight: 500;

    @media (max-width: 480px) {
      margin: 0 0 20px 2px;
    }
  }

  h2 {
    font-size: clamp(40px, 8vw, 80px);
  }

  .gradient-heading {
    background: linear-gradient(90deg, #4183C4, #5a9fd4);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
    font-size: clamp(28px, 4vw, 40px);
  }

  .multicolor-gradient {
    background: linear-gradient(90deg, #0a84ff, #bf5af2, #ff375f, #ff9f0a);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
    font-weight: 700;
  }

  .multicolor-gradient2 {
    background: linear-gradient(90deg, #fcd4b9, #dea4f8, #7db5ff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
    font-weight: 700;
  }

  .subtle-gradient {
    background: linear-gradient(90deg, #4183C4, #5a9fd4);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
    font-weight: 600;
  }

  .formal-gradient {
    background: linear-gradient(90deg, #4183C4, #5a9fd4);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
    font-weight: 600;
  }

  h3 {
    margin-top: 10px;
    line-height: 1.3;
  }

  h3.typewriter-heading {
    font-size: clamp(28px, 4vw, 40px);
    color: var(--slate);
    position: relative;
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    white-space: normal;
    font-family: var(--font-sans);
    animation: typing 3s steps(60, end) forwards;
    width: 0;
  }

  h3.typewriter-heading::after {
    content: '';
    position: absolute;
    right: 0;
    top: 5%;
    height: 70%;
    width: 0.05em;
    background: linear-gradient(180deg, #4183C4, #5a9fd4);
    animation: hide-cursor 0.1s forwards;
    animation-delay: 3s;
  }

  @media (max-width: 480px) {
    h3.typewriter-heading {
      animation: none;
      width: auto;
    }

    h3.typewriter-heading::after {
      display: none;
    }
  }

  @keyframes typing {
    from {
      width: 0;
    }
    to {
      width: 100%;
    }
  }

  @keyframes hide-cursor {
    to {
      opacity: 0;
    }
  }

  p {
    margin: 16px 0 0;
    max-width: none;
    line-height: 1.4;
    color: var(--lightest-slate);
    text-align: left;
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    margin-top: 50px;
  }

.video-container {
  margin-top: 60px;
  border-radius: 15px;
  width: 100%;
  max-width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    vertical-align: top;
  }
}
  }
`;

const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;
    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, []);

  const one = <h1 className="formal-gradient">Hi, my name is</h1>;
  const two = <h2 className="big-heading multicolor-gradient2">Akash Patel.</h2>;
  const three = (
    <h3 className="gradient-heading">
      I build reliable autonomy for robots in unreliable environments.
    </h3>
  );
  const four = (
    <div>
      <p>
        I am a robotics PhD Candidate at{' '}
        <a href="https://www.fieldrobotics.eu/" target="_blank" rel="noreferrer"> LTU-RAI </a>. 
        I develop autonomous systems that enable robots to operate independently in environments where GPS is unavailable, terrain is unknown, and reliability is critical.
      </p>
      <p>
        My focus spans mission planning, real-time path planning, and building robust autonomy software designed for deployment in challenging, real-world conditions.
      </p>
    </div>
  );

const video = (
  <div className="video-container">
    <video
      src="/stage_video.mp4"
      autoPlay
      muted
      loop
      playsInline
      controls={false}
      preload="auto"
      aria-label="Drone flying in a lava tube cave"
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  </div>
);

  const items = [one, two, three, four];

  return (
    <StyledHeroSection>
      {prefersReducedMotion ? (
        <>
          {items.map((item, i) => (
            <div key={i}>{item}</div>
          ))}
          {video}
        </>
      ) : (
        <TransitionGroup component={null}>
          {isMounted &&
            items.map((item, i) => (
              <CSSTransition key={i} classNames="fadeup" timeout={loaderDelay}>
                <div style={{ transitionDelay: `${i + 1}00ms` }}>{item}</div>
              </CSSTransition>
            ))}
          {isMounted && (
            <CSSTransition classNames="fadeup" timeout={loaderDelay}>
              <div style={{ transitionDelay: `${items.length + 1}00ms` }}>{video}</div>
            </CSSTransition>
          )}
        </TransitionGroup>
      )}
    </StyledHeroSection>
  );
};

export default Hero;
